import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Check,
  History,
  Lightning,
  Loading2,
  Pencil,
  Plus,
} from 'assets/icons';
import Button from 'components/button';
import PageForm from 'components/pageForm/PageForm';
import CallList from 'components/prospection/CallList';
import CallSheetPlaceholder from 'components/prospection/CallSheetPlaceholder';
import GlobalListPanel from 'components/prospection/GlobalListPanel';
import IdlePanel from 'components/prospection/IdlePanel';
import SidePanel from 'components/prospection/SidePanel';
import UndoToast from 'components/toasts/UndoToast';
import {
  isCanceledRequest,
  prospectionErrorText,
} from 'helper/prospectionErrors';
import useText from 'hooks/useText';
import {
  dayState,
  lineOfCompany,
  postponedToast,
  telHref,
} from 'pages/prospection/callListItems';
import useCallDay from 'pages/prospection/useCallDay';
import {
  addToCallDay,
  removeFromCallDay,
  restoreToCallDay,
} from 'server/crm/prospectionApi';

const ENTRY_PARAMS = ['prospect', 'appeler'];
const COMMAND_CLASS = 'disabled:cursor-not-allowed disabled:opacity-50';

// Through a link rather than location.href: the page stays where it is while
// the phone application takes the number.
const dial = (phone) => {
  const href = telHref(phone);
  if (!href) return;
  const link = document.createElement('a');
  link.href = href;
  link.click();
};

// Today's call list (JBWEB-1394): the list on the left, and on the right the
// call sheet, the waiting panel or the end panel.
const ProspectionPage = () => {
  const { text } = useText('prospection');
  const {
    day,
    now,
    loading,
    error,
    reloadDay,
    globalProspects,
    globalLoading,
    reloadGlobal,
  } = useCallDay();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [openCall, setOpenCall] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [globalOpen, setGlobalOpen] = useState(false);
  // The company of ?appeler=, kept until the day and the global list are read.
  const pendingCallId = useRef(null);

  // Links from other screens: read once, then taken out of the address.
  useEffect(() => {
    if (!ENTRY_PARAMS.some((name) => searchParams.has(name))) return;
    const prospect = searchParams.get('prospect');
    const appeler = searchParams.get('appeler');
    if (prospect) setHighlightId(prospect);
    if (appeler) pendingCallId.current = appeler;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        ENTRY_PARAMS.forEach((name) => next.delete(name));
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const companyId = pendingCallId.current;
    if (!companyId || !day || globalProspects === null) return;
    pendingCallId.current = null;
    const line = lineOfCompany(day.pending, companyId);
    if (line) {
      setOpenCall({ company: line.company, line });
      return;
    }
    // Out of today's list: the sheet opens on its own (`direct`).
    const prospect = globalProspects.find(
      (item) => item.company?.id === companyId,
    );
    if (prospect) setOpenCall({ company: prospect.company, line: null });
  }, [day, globalProspects]);

  const comingSoon = useCallback(() => toast.info(text('comingSoon')), [text]);
  const fail = useCallback(
    (failure) => {
      if (isCanceledRequest(failure)) return;
      toast.error(prospectionErrorText(text, failure));
    },
    [text],
  );
  const reloadAll = () => {
    reloadDay();
    reloadGlobal();
  };

  const call = (line) => {
    setEditing(false);
    dial(line.company?.phone);
    setOpenCall({ company: line.company, line });
  };

  const launchSession = () => {
    setEditing(false);
    comingSoon();
  };

  const restore = (undo) => restoreToCallDay(undo).then(reloadAll, fail);

  const remove = (line) =>
    removeFromCallDay({ callbackId: line.id }).then((result) => {
      reloadAll();
      const company = line.company?.name || '-';
      let message = text('toasts.removed', { company });
      if (result?.removed === 'postponed') {
        const { key, values } = postponedToast(result.dueAt, new Date());
        message = text(key, { ...values, company });
      }
      toast.success(
        <UndoToast
          message={message}
          undoLabel={text('toasts.undo')}
          onUndo={() => restore(result.undo)}
        />,
      );
    }, fail);

  const addSuggestion = (prospect) =>
    addToCallDay({ companyId: prospect.company.id }).then(() => {
      toast.success(
        text('toasts.added', { company: prospect.company.name || '-' }),
      );
      reloadAll();
    }, fail);

  // Rejected when the add fails, so that the panel keeps its button.
  const addFromGlobal = (prospect) =>
    addToCallDay({ companyId: prospect.company.id }).then(
      () => {
        reloadDay();
      },
      (failure) => {
        fail(failure);
        throw failure;
      },
    );

  const closeGlobal = () => {
    setGlobalOpen(false);
    reloadGlobal();
  };

  const openCompanyId = openCall?.company?.id ?? null;
  const highlightedLine =
    day && highlightId ? lineOfCompany(day.pending, highlightId) : null;

  const renderSide = () => {
    if (openCall) {
      return (
        <CallSheetPlaceholder
          company={openCall.company}
          onClose={() => setOpenCall(null)}
        />
      );
    }
    if (!day) {
      return loading ? (
        <div className="flex h-full items-center justify-center py-16">
          <Loading2 wh={32} color="#9CA3AF" />
        </div>
      ) : null;
    }
    if (day.pending?.length) {
      return (
        <IdlePanel
          pending={day.pending}
          highlightedLine={highlightedLine}
          onLaunch={launchSession}
          onCall={call}
        />
      );
    }
    return (
      <SidePanel
        day={day}
        state={dayState(day)}
        prospects={globalProspects}
        now={now}
        onAdd={addSuggestion}
      />
    );
  };

  const title = (
    <span className="flex items-center gap-2">
      <Lightning wh={20} color="currentColor" />
      {text('title')}
    </span>
  );

  return (
    <PageForm title={title}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex h-[72px] shrink-0 items-center justify-end gap-3 border-b border-gray-100 px-4 lg:px-8">
          <Button color="SECONDARY" size="SM" onClick={comingSoon}>
            <History wh={16} color="currentColor" />
            {text('commands.sessions')}
          </Button>
          {editing ? (
            <Button
              color="PRIMARY"
              size="SM"
              disabled={loading}
              className={COMMAND_CLASS}
              onClick={() => setEditing(false)}
            >
              <Check wh={16} color="currentColor" />
              {text('commands.finishEdit')}
            </Button>
          ) : (
            <Button
              color="SECONDARY"
              size="SM"
              disabled={loading}
              className={COMMAND_CLASS}
              onClick={() => setEditing(true)}
            >
              <Pencil wh={16} color="currentColor" />
              {text('commands.edit')}
            </Button>
          )}
          <Button
            color="PRIMARY"
            size="SM"
            disabled={loading}
            className={COMMAND_CLASS}
            onClick={comingSoon}
          >
            <Plus wh={16} color="currentColor" />
            {text('commands.addProspect')}
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 gap-6 px-4 py-6 lg:px-8">
          <section className="w-[520px] shrink-0 overflow-y-auto">
            <CallList
              day={day}
              loading={loading}
              error={error}
              now={now}
              editing={editing}
              selectedCompanyId={openCompanyId ?? highlightId}
              openCompanyId={openCompanyId}
              queueActions={null}
              onCall={call}
              onRemove={remove}
              onOpenGlobal={() => setGlobalOpen(true)}
              onAddProspect={comingSoon}
            />
          </section>
          <section className="min-w-0 flex-1 overflow-y-auto">
            <div className="min-h-full rounded-xl border border-gray-100 bg-white p-6">
              {renderSide()}
            </div>
          </section>
        </div>
      </div>
      <GlobalListPanel
        open={globalOpen}
        onClose={closeGlobal}
        prospects={globalProspects}
        loading={globalLoading}
        counts={day?.counts}
        onAdd={addFromGlobal}
        onShowAllProspects={comingSoon}
      />
    </PageForm>
  );
};

export default ProspectionPage;
